import React, { useCallback, useState } from 'react'
import context from '@/context'
import { ValidationBoundary } from '@/components/validation'
import { ModalBody, Row } from 'reactstrap'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import LoadingIndicator from '@/components/LoadingIndicator'
import { TabNavItems, TabContent, TabPane } from '@/components/tabs'
import { FormLoader, FormRow } from '@/components/changeRequests'
import { AddressesBranch } from '@/components/demographics/addresses'
import { BranchContacts } from '@/components/demographics/contacts'
import { ChangeItemDialogFooter } from '@/dialogs/common/changeItemDialogFooter'
import { BranchRealEstates } from '@/components/demographics/realEstates'
import { BranchManagers } from '@/components/demographics/branchManagers'
import { useDataLoader } from '@/hooks/useDataLoader'
import { DualLanguageRow } from '@/components/changeRequests/dualLanguageRow'
import { PermissionSectionProvider } from '@/components/changeRequests/institutionPermissionProvider'
import { institutionPermissionTypes } from '@/enums/institutionPermissionTypes'

export const BranchDialog = React.memo(function BranchDialog (props) {
  const { isOriginal, loadData, value } = props
  const dataSource = useCallback(() => loadData(value.id), [loadData, value.id])
  const { data, loading } = useDataLoader(dataSource)
  return (
    <ValidationBoundary>
      <BasicHeader title='Institutions.Branch' {...props} />
      {!isOriginal
        ? <BranchFormWrapper {...props} />
        : loading ? <ModalBody><LoadingIndicator /></ModalBody> : <BranchFormWrapper {...props} id={data.id} value={data} />
      }
    </ValidationBoundary>
  )
})

function BranchFormWrapper (props) {
  return (
    <FormLoader {...props} render={({ targetId }) => <BranchForm {...props} targetId={targetId} />} />
  )
}

const BranchForm = React.memo(function BranchForm (props) {
  const { targetId, target } = props
  const [activeTabId, setActiveTabId] = useState('1')

  const getAddresses = useCallback(req => context.api.branchService.getAddresses(targetId, req), [targetId])
  const getContacts = useCallback(req => context.api.branchService.getContacts(targetId, req), [targetId])
  const getRealEstates = useCallback(req => context.api.branchService.getRealEstates(targetId, req), [targetId])
  const getRelatedPersons = useCallback(req => context.api.branchService.getRelatedPersons(targetId, req), [targetId])

  return (
    <React.Fragment>
      <ModalBody>
        <React.Fragment>
          <TabNavItems activeTabId={activeTabId} onClick={setActiveTabId} items={[
            { id: '1', text: 'Common.Details' },
            { id: '2', text: 'Persons.Addresses' },
            { id: '3', text: 'Persons.Contacts' },
            { id: '4', text: 'Institutions.RealEstate' },
            { id: '5', text: 'Institutions.BranchManagers' }
          ]} />
          <TabContent activeTab={activeTabId} renderActiveOnly>
            <TabPane tabId='1'>
              <PermissionSectionProvider institutionPermission={institutionPermissionTypes.enum.Branches}>
                <Row>
                  <div className='col-6'>
                    <FormRow label='Common.Type' name='branchType' target={target} inputType='lookup' dataSource={context.cache.getBranchTypes} clientSide isCodeList required />
                    <FormRow label='Common.Status' name='status' target={target} inputType='lookup' dataSource={context.cache.getPersonStatuses} clientSide isCodeList required />
                    <DualLanguageRow label='Common.ArabicName' latinLabel='Common.LatinName' name={'name'} latinName={'latinName'} target={target} inputProps={{ maxLength: 100 }} required />
                    <FormRow label='Common.Code' name='code' target={target} required inputProps={{ maxLength: 100 }} />
                  </div>
                  <div className='col-6'>
                    <FormRow label='Institutions.DateOfApproval' name='functionalSince' target={target} inputType='date' required />
                    <FormRow label='Institutions.FinalApprovalNumber' name='finalApprovalNumber' target={target} />
                    <FormRow label='Institutions.IncorporationCode' name='incorporationCode' target={target} inputProps={{ maxLength: 100 }} />
                    <FormRow label='Institutions.IncorporationState' name='incorporationState' target={target} inputType='lookup' dataSource={context.cache.getIncorporationStates} clientSide isCodeList />
                  </div>
                </Row>
                <div className='row'>
                  <div className='col-12'>
                    <FormRow label='Common.Description' name='description' target={target} inputType='textarea' inputProps={{ rows: 4 }} />
                  </div>
                </div>
              </PermissionSectionProvider>
            </TabPane>
            <TabPane tabId='2'>
              <AddressesBranch dataSource={getAddresses} />
            </TabPane>
            <TabPane tabId='3'>
              <BranchContacts dataSource={getContacts} />
            </TabPane>
            <TabPane tabId='4'>
              <BranchRealEstates dataSource={getRealEstates} />
            </TabPane>
            <TabPane tabId='5'>
              <BranchManagers branchId={targetId} dataSource={getRelatedPersons} />
            </TabPane>
          </TabContent>
        </React.Fragment>
      </ModalBody>
      <ChangeItemDialogFooter {...props} />
    </React.Fragment>
  )
})

export default {
  modalProps: {
    size: 'lg'
  },
  component: BranchDialog
}



// WEBPACK FOOTER //
// ./src/dialogs/branch.js